import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import type { EmailContext } from "../../email/types";
import { findMeetingEmail, inviteAsEmail } from "../services/agendaDraft";
import { writePrepBrief, type BriefLine } from "../services/prepBrief";

/** What the brief is written for. `facts` are the pane's own lines, which AiVA rewrites. */
export interface PrepBriefRequest {
  readonly eventId: string;
  readonly subject: string;
  readonly organizerEmail: string;
  readonly attendeeEmails: readonly string[];
  readonly inviteText: string;
  readonly facts: readonly BriefLine[];
  readonly userDisplayName: string;
}

export type PrepBriefState =
  | { readonly status: "idle" }
  | { readonly status: "writing"; readonly eventId: string }
  | { readonly status: "done"; readonly eventId: string; readonly lines: readonly BriefLine[] }
  /** AiVA couldn't write it: the screen shows the pane's own facts instead, with `message`. */
  | { readonly status: "failed"; readonly eventId: string; readonly message: string };

/**
 * Prepare for This Meeting, written by AiVA from the meeting's email thread
 * (or its invite) and the pane's own facts. A brief still being written is
 * cancelled when another starts or the meeting screen closes.
 */
export function usePrepBrief() {
  const { apiToken, graphToken, refreshTokens, refreshGraphToken } = useAuthState();
  const [state, setState] = useState<PrepBriefState>({ status: "idle" });
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const start = useCallback(
    async (request: PrepBriefRequest) => {
      const backendToken = apiToken ?? graphToken;
      abortRef.current?.abort();
      if (!backendToken || !graphToken) {
        setState({ status: "failed", eventId: request.eventId, message: "Sign in to AiVA for a written brief." });
        return;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setState({ status: "writing", eventId: request.eventId });

      let email: EmailContext | null = null;
      try {
        email = await findMeetingEmail(graphToken, () => refreshGraphToken(false), request.subject);
      } catch (e: unknown) {
        logConnectorFailure("calendar", e, "find-meeting-email");
      }
      if (controller.signal.aborted) return;

      try {
        const lines = await writePrepBrief({
          apiToken: backendToken,
          refresh: refreshTokens,
          email:
            email ??
            inviteAsEmail({
              subject: request.subject,
              organizerEmail: request.organizerEmail,
              attendees: request.attendeeEmails,
              inviteText: request.inviteText,
            }),
          subject: request.subject,
          facts: request.facts,
          userDisplayName: request.userDisplayName,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setState({ status: "done", eventId: request.eventId, lines });
        logConnectorSuccess("calendar", "prep-brief");
      } catch (e: unknown) {
        if (controller.signal.aborted) return;
        setState({
          status: "failed",
          eventId: request.eventId,
          message: e instanceof Error ? e.message : "AiVA couldn't write the brief.",
        });
        logConnectorFailure("calendar", e, "prep-brief");
      }
    },
    [apiToken, graphToken, refreshTokens, refreshGraphToken],
  );

  return { state, start };
}
